import { useMemo } from 'react';
import type { ComplianceCheck } from '@/services';
import type { ComplianceLineItem } from '@/services';
import { summarize, fromDbRow } from '@prolarus/shared';
import { formatDistanceToNow } from 'date-fns';

type NonComplianceFilter = 'qtyMismatch' | 'priceMismatch' | 'unsupportedItem' | 'noTicketSupport' | null;

interface InvoiceComplianceCardsProps {
  complianceChecks: ComplianceCheck[];
  onCardClick?: (filter: NonComplianceFilter) => void;
  lastCheckTime?: string | null;
  engineLineItems?: ComplianceLineItem[];
}

export function InvoiceComplianceCards({
  complianceChecks,
  onCardClick,
  lastCheckTime,
  engineLineItems,
}: InvoiceComplianceCardsProps) {
  const summary = useMemo(() => {
    const results = complianceChecks.map(fromDbRow);
    return summarize(results);
  }, [complianceChecks]);

  const qtyMismatchCount = complianceChecks.filter(c => c.qty_mismatch_on_ticket_item).length;
  const priceMismatchCount = complianceChecks.filter(
    c => c.price_mismatch_on_price_book_item || c.price_mismatch_on_ticket_item,
  ).length;
  const unsupportedCount = complianceChecks.filter(
    c => c.missing_price_book_item,
  ).length;
  const noTicketCount = complianceChecks.filter(c => c.missing_ticket_item).length;

  const totalQtyOvercharge = summary.totalQtyOverchargeFromTicket;
  const totalPriceOvercharge = summary.totalPriceOverchargeFromPb + summary.totalPriceOverchargeFromTicket;
  const totalNoTicketOvercharge = summary.totalNoTicketItemOvercharge;

  const unsupportedEngineOvercharge = engineLineItems?.length
    ? engineLineItems
        .filter(i => i.checks.contract_item === 'FAIL' && i.types === 'overcharge' && i.total_impact != null)
        .reduce((s, i) => s + i.total_impact!, 0) || null
    : null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const noChecks = complianceChecks.length === 0;

  return (
    <div className="bg-card border border-border rounded-lg p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Surface Level Results</h2>
        {lastCheckTime && (
          <span className="text-xs text-muted-foreground">
            Checked {formatDistanceToNow(new Date(lastCheckTime), { addSuffix: true })}
          </span>
        )}
      </div>

      {noChecks ? (
        <div className="text-center py-4 text-sm text-muted-foreground">
          No surface-level checks have been run yet. Click "Run Surface Checks" above to analyze this invoice.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {/* Quantity Mismatches */}
          <div
            className={`p-3 bg-muted/50 rounded-lg${onCardClick ? ' cursor-pointer hover:border-primary/50 transition-colors border border-transparent' : ''}`}
            onClick={() => onCardClick?.('qtyMismatch')}
          >
            <div className="text-xs text-muted-foreground mb-1">Qty Mismatches</div>
            <div className="text-lg font-semibold">{qtyMismatchCount} items</div>
            {totalQtyOvercharge > 0 && (
              <div className="text-xs text-destructive mt-1">
                +{formatCurrency(totalQtyOvercharge)}
              </div>
            )}
          </div>

          {/* Price Mismatches */}
          <div
            className={`p-3 bg-muted/50 rounded-lg${onCardClick ? ' cursor-pointer hover:border-primary/50 transition-colors border border-transparent' : ''}`}
            onClick={() => onCardClick?.('priceMismatch')}
          >
            <div className="text-xs text-muted-foreground mb-1">Price Mismatches</div>
            <div className="text-lg font-semibold">{priceMismatchCount} items</div>
            {totalPriceOvercharge > 0 && (
              <div className="text-xs text-destructive mt-1">
                +{formatCurrency(totalPriceOvercharge)}
              </div>
            )}
          </div>

          {/* Non-contract Items */}
          <div
            className={`p-3 bg-muted/50 rounded-lg${onCardClick ? ' cursor-pointer hover:border-primary/50 transition-colors border border-transparent' : ''}`}
            onClick={() => onCardClick?.('unsupportedItem')}
          >
            <div className="text-xs text-muted-foreground mb-1">Non-contract Items</div>
            <div className="text-lg font-semibold">{unsupportedCount} items</div>
            {unsupportedEngineOvercharge != null && unsupportedEngineOvercharge > 0 && (
              <div className="text-xs text-destructive mt-1">
                +{formatCurrency(unsupportedEngineOvercharge)}
              </div>
            )}
          </div>

          {/* No Ticket Support */}
          <div
            className={`p-3 bg-muted/50 rounded-lg${onCardClick ? ' cursor-pointer hover:border-primary/50 transition-colors border border-transparent' : ''}`}
            onClick={() => onCardClick?.('noTicketSupport')}
          >
            <div className="text-xs text-muted-foreground mb-1">No Ticket Support</div>
            <div className="text-lg font-semibold">{noTicketCount} items</div>
            {totalNoTicketOvercharge > 0 && (
              <div className="text-xs text-destructive mt-1">
                +{formatCurrency(totalNoTicketOvercharge)}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
